import {
    TOrderField,
    TWorksheetFieldSyncPayload,
} from 'types/worksheets/worksheetFields';
import { TWorksheetFields } from 'types/worksheets/worksheets';

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import type { WorksheetFieldFlagKey } from '../types';

type TWorksheetFieldFlagsSnapshot = Pick<
    TOrderField,
    'id' | 'is_used' | 'is_required' | 'is_saved' | 'is_photo'
>[];

function snapshotFromOrderFields(order_fields: TOrderField[]): TWorksheetFieldFlagsSnapshot {
    return order_fields.map(({ id, is_used, is_required, is_saved, is_photo }) => ({
        id,
        is_used,
        is_required,
        is_saved,
        is_photo,
    }));
}

function normalizeWorksheetOrderFields(worksheet: TWorksheetFields): TWorksheetFields {
    if (!worksheet.order_fields?.length) return worksheet;
    return {
        ...worksheet,
        order_fields: worksheet.order_fields.map((f) => ({
            ...f,
            is_used: f.is_used ?? true,
        })),
    };
}

interface InitialState {
    worksheetFields: TWorksheetFields | null;
    worksheetFieldsFlagsSnapshot: TWorksheetFieldFlagsSnapshot | null;
}

const initialState: InitialState = {
    worksheetFields: null,
    worksheetFieldsFlagsSnapshot: null,
};

interface SetWorksheetFieldFlagPayload {
    key: WorksheetFieldFlagKey;
    fieldIndex: number;
    value: boolean;
}

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        setWorksheetFields: (state, action: PayloadAction<TWorksheetFields>) => {
            const normalized = normalizeWorksheetOrderFields(action.payload);
            state.worksheetFields = normalized;
            state.worksheetFieldsFlagsSnapshot = normalized?.order_fields?.length
                ? snapshotFromOrderFields(normalized.order_fields)
                : null;
        },

        setWorksheetFieldFlag: (state, action: PayloadAction<SetWorksheetFieldFlagPayload>) => {
            const { key, value, fieldIndex } = action.payload;
            const row = state.worksheetFields?.order_fields?.[fieldIndex];
            if (!row) return;

            row[key] = value;

            if (key === 'is_used') {
                if (!value) {
                    row.is_required = false;
                    row.is_saved = false;
                    row.is_photo = false;
                }
            }
        },

        setOrderFields: (state, action: PayloadAction<TOrderField[]>) => {
            if (!state.worksheetFields) return;
            state.worksheetFields.order_fields = action.payload;
            state.worksheetFieldsFlagsSnapshot = snapshotFromOrderFields(action.payload);
        },
    },
});

export function orderFieldsToSyncPayload(
    fields: TOrderField[]
): TWorksheetFieldSyncPayload[] {
    return fields.map((f) => ({
        boiler_field_id: f.boiler_field_id ?? f.id,
        is_used: f.is_used,
        is_required: f.is_required,
        is_saved: f.is_saved,
        is_photo: f.is_photo,
    }));
}

export const { setWorksheetFields, setWorksheetFieldFlag, setOrderFields } =
    settingsSlice.actions;
export default settingsSlice.reducer;
